import { Affiliation } from './Affiliation';
import { Plan } from './Plan';
import { UserCreditInfo } from './UserCreditInfo';
import { UserMonitoredPhone } from './UserMonitoredPhone';

export interface User {
  /** CU-86ak84mj6 — preenchido quando o usuário confirmou o segundo fator. */
  two_factor_confirmed_at?: string;
  id: string;
  name: string;
  email: string;
  phone: string;
  document: string;
  password: string;
  sms_token_validated: boolean;
  active: boolean;
  credit_payment_enabled: boolean;
  temporary: boolean;
  promocode: string;
  validated: boolean;
  massive_sms_enabled: boolean;
  integration_sms_enabled: boolean;
  /** CU-86ak8p7w0 — a conta recusa postback e add-lead sem o token da URL. Só admin altera. */
  postback_token_required?: boolean;
  /** Multiusuários liberado pelo administrador (CU-17tm466h1cm). */
  multiuser_enabled?: boolean;
  /**
   * CU-17tm466tzkz — "Recebe bônus de recarga" (`users.recharge_bonus_enabled`).
   * Ligada por padrão; desligada, a recarga que o próprio cliente faz não ganha a
   * faixa e a recarga manual do admin vem com o switch desmarcado. Só admin altera.
   */
  recharge_bonus_enabled?: boolean | number | string;
  revision_sms_enabled: boolean;
  leads_export: boolean;
  affiliate_code: string;
  pix_key: string;
  utm_source: string;
  profile_id: string;
  commission: number;
  /**
   * Resíduo da era Twilio: o backend não devolve `global`, então a condição
   * nunca é verdadeira. Quem libera o switch de versão é `intl_enabled`.
   */
  global: boolean;
  globalActivated: boolean;
  /** Conta tem acesso à versão internacional. Ausente = não tem. */
  intl_enabled?: boolean;
  pix_priority: boolean;
  whatsapp_enabled: boolean;
  /** N integrações de WhatsApp (CU-17tm466mfzu): flag de liberação e teto (só admin altera). */
  whatsapp_multi_accounts?: boolean;
  max_whatsapp_accounts?: number;
  broadcast_provider_account_id: string;
  automation_provider_account_id: string;
  broadcast_fractionation_route: string;
  automation_fractionation_route: string;
  migration_in_progress: boolean;

  operation_type: string;
  reference_user: User;

  plan: Plan;
  affiliation: Affiliation;

  city: string;
  complement: string;
  district: string;
  number: number;
  postcode: string;
  region: string;
  street: string;

  credits?: number;
  total_sent?: number;
  total_contracted?: number;

  call_credits?: number;

  created_at: Date;
  updated_at: Date;

  credit_info: UserCreditInfo;
  simulated: boolean;

  call4u_user_id: number;

  analytics_sync: boolean;
  analytics_sales_sync: boolean;
  fake_request: boolean;
  delivery_report_enabled: boolean;
  funnel_report_enabled: boolean;
  disable_utm_source: boolean;
  show_funnel_report: boolean;
  show_sales_report: boolean;
  broadcast_send_interval: boolean;

  user_created_at?: Date;
  is_billable: boolean;
  billing_document: string;
  billing_company_name: string;
  billing_company_alias: string;
  billing_email: string;
  billing_im: string;
  billing_postcode: string;
  billing_street: string;
  billing_number: string;
  billing_complement: string;
  billing_district: string;
  billing_city: string;
  billing_state: string;
  billing_update: boolean;
  billing_last_update: Date;
  content_tag_enabled: boolean;
  monitored_phones: UserMonitoredPhone[];

  api_enabled: boolean;
  ai_enabled?: boolean;
  unit_send_cost?: number | null;
  /** Canal RCS ligado na conta (task 17tm466frtx). Mesmo corte do middleware `rcs.enabled`. */
  rcs_enabled?: boolean;
  /**
   * Responsável comercial do cliente (task 17tm466frtx, acompanhamento de agentes): id de um
   * admin. Editado em `UserRcsSettingsComponent` por `PUT /admin/rcs/clients/{id}/account-manager`
   * — rota própria, e não o PUT genérico do usuário (o contrato deixa as duas em aberto; a
   * dedicada evita depender do backend aceitar o campo no corpo do update geral). `null` = sem
   * responsável (cai nos admins do RCS, como hoje).
   */
  account_manager_id?: string | null;
  /**
   * Coorte da carteira em R$ (CU-86akm3435). É a coluna `users.wallet_billing_enabled`,
   * que o login devolve dentro de `user`. Ausente = conta antiga, que continua
   * cobrando pelos baldes de unidade.
   */
  wallet_billing_enabled?: boolean;
  /**
   * Quem paga SMS / ligação desta conta é a carteira (colunas `users.wallet_sms_billed` e
   * `users.wallet_call_billed`, ligadas na migração do produto). A busca de usuários do admin
   * devolve as duas. Ausente = pacote.
   */
  wallet_sms_billed?: boolean;
  wallet_call_billed?: boolean;
  unit_cost_sms?: number | null;
  can_propose_integrations?: boolean;

  /**
   * Saldo decorado pelo `ManagedAccountBalanceService` (CU-17tm466u6ga) — mesmo formato do
   * `/credits`. Só vem preenchido em listagens que decoram saldo (clientes do admin); ausente
   * nas demais chamadas que devolvem `User` (não é "sem saldo", é "esta tela não pediu").
   */
  balance_kind?: 'wallet' | 'credits';
  wallet?: import('./ManagedBalance').WalletBlock;
  sms?: import('./ManagedBalance').CreditsBlock;
  call?: import('./ManagedBalance').CreditsBlock;
  sms_equivalent?: number | null;
  balance_level?: import('./ManagedBalance').BalanceLevel;
}

/**
 * `recharge_bonus_enabled` como o backend pode mandar (CU-17tm466tzkz, review 4): boolean
 * (o `User` do backend tem o cast), mas também `0`/`1` ou `'0'`/`'1'` — tinyint cru de uma
 * consulta sem o cast. `!== false` tratava o `0` como LIGADO, e o próximo Salvar religava o
 * bônus de uma conta opt-out. `null` = o valor não veio (ou não é reconhecível).
 */
export function parseRechargeBonusFlag(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === 1 || value === '1' || value === 'true') {
    return true;
  }
  if (value === 0 || value === '0' || value === 'false') {
    return false;
  }
  return null;
}

/** A conta recebe bônus de recarga? Sem o campo (backend anterior à config) = recebe, o padrão. */
export function receivesRechargeBonus(value: unknown): boolean {
  return parseRechargeBonusFlag(value) ?? true;
}
